/* FERAL FIT — engine + size guide UI. Built by ~/feral-fit/build/build.sh from engine/feral-fit-engine.js + build/src/ui.js */
/*
  FERAL FIT ENGINE — one way of working out a size, for every garment.

  1. A body from what the shopper tells us. Height, weight and sex predict chest, waist,
     hip, inseam, back length and shoulder from regressions fitted on ANSUR II (US Army
     anthropometric survey 2012, public: 4,082 men, 1,986 women). Measured error of the
     prediction, held-out: chest ±3.2 cm (men) ±4.2 (women), waist ±4.4/±4.8, hip ±2.6/±2.9,
     inseam ±2.3. Two optional answers (tummy, hips) move the body inside that error the way
     real bodies move: a rounder tummy also carries some chest, curvier hips some waist.
     A tape measurement replaces the guess for that measure.
  2. The garment as the factory made it: full circumferences and lengths in cm, per size.
  3. A rule per measure for how much room this garment is drafted to have over the body
     (ease), for each fit the shopper can ask for, and how far either side still reads right.
  4. Not one guess but 600 plausible bodies drawn inside the error, each scored against every
     size. The share of bodies each size wins is the confidence we tell the shopper, and two
     sizes that split the bodies are "between sizes", said out loud.

  The engine knows nothing about any one product; a chart carries its numbers and rules.
*/
(function (root) {
  'use strict';

  // ANSUR II regressions: y = c0 + c1·h + c2·w + c3·BMI + c4·ln(w)   (cm, kg)
  var MODEL = {"m":{"c":{"chest":[-17.26583,-0.11874,0.13949,0.56733,26.23454],"waist":[-5.6683,-0.05355,0.26403,1.23644,11.79605],"hip":[-17.67804,0.00924,0.1363,0.49126,20.91223],"inseam":[-11.62369,0.64477,-0.01818,0.07782,-3.97255],"torso":[7.50863,0.28236,-0.01124,0.4813,-4.89275],"shoulder":[7.45541,0.2009,-0.03295,0.35077,-1.8182],"waistH":[-27.56015,0.62374,-0.02656,-0.38563,8.2555],"trunk":[-13.32404,0.27052,0.011,0.43835,8.11211],"thigh":[-16.79043,-0.33275,0.21937,-0.46565,29.73396]},"sd":{"chest":3.17,"waist":4.36,"hip":2.58,"inseam":2.34,"torso":2.07,"shoulder":1.44,"waistH":2.06,"trunk":1.98,"thigh":2.2},"shape":{"belly":{"chest":0.149,"hip":0.08},"hips":{"chest":-0.373,"waist":0.229}}},"f":{"c":{"chest":[2.20574,0.11575,0.08287,1.43362,7.48609],"waist":[-10.86743,-0.05646,0.20608,1.34486,13.77588],"hip":[-27.21319,0.03512,0.07715,0.70831,23.87115],"inseam":[-7.34289,0.59831,0.03778,-0.07228,-2.99195],"torso":[27.02224,0.28552,0.07006,0.53457,-11.7426],"shoulder":[-9.05906,0.15424,-0.07762,0.08877,5.58439],"waistH":[-20.71135,0.77168,-0.05782,0.02001,-0.84021],"trunk":[3.96091,0.33926,0.00356,0.62472,0.61702],"thigh":[-17.87424,-0.25169,0.19057,-0.15763,26.54783]},"sd":{"chest":4.23,"waist":4.81,"hip":2.89,"inseam":2.34,"torso":2.07,"shoulder":1.47,"waistH":2.0,"trunk":1.88,"thigh":2.23},"shape":{"belly":{"chest":0.327,"hip":0.008},"hips":{"chest":-0.347,"waist":0.021}}}};

  var DIMS = ['chest', 'waist', 'hip', 'inseam', 'torso', 'shoulder', 'waistH', 'trunk', 'thigh'];
  var LENGTHS = { inseam: true, torso: true, waistH: true };

  function predict(sex, k, h, w) {
    var c = MODEL[sex].c[k], b = w / Math.pow(h / 100, 2);
    return c[0] + c[1] * h + c[2] * w + c[3] * b + c[4] * Math.log(w);
  }

  // Deterministic normal draws, so the same answers always give the same advice.
  function rng(seed) {
    var s = seed >>> 0;
    return function () {
      s = (s + 0x6D2B79F5) >>> 0;
      var t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function gauss(r) {
    var u = 0, v = 0;
    while (u === 0) u = r();
    while (v === 0) v = r();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  /* input: { sex: 'm'|'f'|'x', heightCm, weightKg, tummy: -1|0|1, hips: -1|0|1,
              measured: { chest, waist, hip, inseam } }  (any measured value in cm)
     returns the expected body and its error per measure. */
  function body(input) {
    var sexes = input.sex === 'm' ? ['m'] : input.sex === 'f' ? ['f'] : ['m', 'f'];
    var h = +input.heightCm, w = +input.weightKg;
    var out = { mean: {}, sd: {}, sex: input.sex || 'x' };
    // Lengths are read at the shopper's height and a middling build (BMI 22). In ANSUR a heavier body
    // of the same height has a waist point a few millimetres lower, which is real but tells a garment
    // nothing, and it let a heavier shopper be offered a smaller trouser (check.js, 2026-10-18).
    var wl = 22 * Math.pow(h / 100, 2);
    DIMS.forEach(function (k) {
      var mu = 0, sd = 0, wk = LENGTHS[k] ? wl : w;
      sexes.forEach(function (s) { mu += predict(s, k, h, wk); sd += MODEL[s].sd[k]; });
      mu /= sexes.length; sd /= sexes.length;
      if (sexes.length === 2) {
        // not told: the two predictions differ, and that difference is real uncertainty
        var d = predict('m', k, h, wk) - predict('f', k, h, wk);
        sd = Math.sqrt(sd * sd + d * d / 4);
      }
      out.mean[k] = mu; out.sd[k] = sd;
    });
    // tummy / hips answers: about 0.8 of the remaining spread either way
    var shape = MODEL[sexes[0]].shape;
    var tz = +(input.tummy || 0) * 0.8, hz = +(input.hips || 0) * 0.8;
    if (tz) {
      var dw = tz * out.sd.waist;
      out.mean.waist += dw;
      out.mean.chest += dw * shape.belly.chest;
      out.mean.hip += dw * shape.belly.hip;
      out.sd.waist *= 0.7;
    }
    if (hz) {
      var dh = hz * out.sd.hip;
      out.mean.hip += dh;
      out.mean.chest += dh * shape.hips.chest;
      out.mean.waist += dh * shape.hips.waist;
      out.sd.hip *= 0.7;
    }
    // no survey measure for the band under the bust: a typical cup apart from the bust
    out.mean.underbust = out.mean.chest - (input.sex === 'm' ? 5 : 12);
    out.sd.underbust = 4;
    // A measure the shopper gave replaces the guess. `src` says how it was given (tape, dress,
    // bra, jeansW): each way is calibrated on its own, because each carries its own bias
    // (MEASURED 2026-10-18: a UK dress size answered on the baby tee came out 1.4 sizes small
    // under the height-and-weight calibration). A dress size or bra size is a band, not a tape.
    out.fixed = {}; out.src = {};
    var m = input.measured || {}, src = input.src || {}, msd = input.measuredSd || {};
    Object.keys(m).forEach(function (k) {
      if (m[k] > 0 && out.mean[k] != null) {
        out.mean[k] = +m[k]; out.sd[k] = msd[k] || 1.5; out.fixed[k] = true; out.src[k] = src[k] || 'tape';
      }
    });
    return out;
  }

  // A body from the size the shopper already wears somewhere else (UK high-street body charts).
  var USUAL = {
    // men's tops by letter: chest, waist (cm, mid of the range)
    m: { XS: [86, 72], S: [93, 78], M: [100, 85], L: [107, 92], XL: [114, 100], XXL: [122, 108] },
    // women's UK dress size: bust, waist, hip
    f: { 4: [76, 60, 84], 6: [80, 63, 87], 8: [83, 66, 90], 10: [88, 70, 95], 12: [93, 75, 100],
         14: [98, 80, 105], 16: [103, 85, 110], 18: [109, 91, 116], 20: [115, 97, 122], 22: [121, 103, 128] }
  };

  function sizeRow(chart, size) {
    for (var i = 0; i < chart.sizes.length; i++) if (chart.sizes[i].size === size) return chart.sizes[i];
    return null;
  }

  /* chart.fit = {
       dims: { <measure>: { key: <size-row key, full cm>, body: <body measure>,
                            ease: { closer, true, oversized }, tol, min, weight, stretch } },
       calib: cm added to every target (from returns: negative when shoppers sent it back too big)
     }
     A dim with `min` is a hard floor: ease below it is a garment that will not close or pulls. */
  // Where a body sits against the slope below: the middle of the shoppers we calibrate on.
  var REF = { f: { chest: 90, waist: 80, hip: 100, underbust: 76 }, m: { chest: 100, waist: 88, hip: 100, underbust: 88 },
              x: { chest: 95, waist: 84, hip: 100, underbust: 82 } };
  /* fit.calibBy = { dress, bra, tape, jeansW }: the offset for a measure given that way (else fit.calib).
     fit.slope: how much less room a bigger body keeps (cm of ease per cm of body over REF). MEASURED
     2026-10-18 on 1,500 kept orders: with none, shoppers under BMI 20 were sized about one size small
     and over BMI 25 about one size big, on nearly every chart. Girth measures only. */
  function score(fit, row, b, pref, sex, src) {
    var cost = 0, reads = {};
    Object.keys(fit.dims).forEach(function (k) {
      var d = fit.dims[k], g = row[d.key], bk = d.body || k;
      if (g == null || b[bk] == null) return;
      var how = src && src[bk], cal = how && fit.calibBy && fit.calibBy[how] != null ? fit.calibBy[how] : (fit.calib || 0);
      var target = (d.ease[pref] != null ? d.ease[pref] : d.ease['true']) + cal * (d.calib === false ? 0 : 1);
      var ref = REF[sex] || REF.x;
      if (fit.slope && ref[bk] != null) target += fit.slope * (ref[bk] - b[bk]);
      var adj = typeof d.bodyAdj === 'object' && d.bodyAdj ? (d.bodyAdj[sex] != null ? d.bodyAdj[sex] : d.bodyAdj.x || 0) : (d.bodyAdj || 0);
      var ease = g + (d.stretch || 0) - (b[d.body || k] + adj);
      var x = (ease - target) / (d.tol || 5);
      var c = x * x * (d.weight == null ? 1 : d.weight);
      if (d.min != null && ease < d.min) c += 40 * Math.pow((d.min - ease) / (d.tol || 5), 2) + 4;
      cost += c;
      reads[k] = { ease: ease, target: target, off: x };
    });
    return { cost: cost, reads: reads };
  }

  function recommend(chart, input, pref, opts) {
    opts = opts || {};
    pref = pref || 'true';
    var fit = chart.fit, sizes = chart.sizes.map(function (s) { return s.size; });
    var stocked = opts.available; // optional { size: bool }
    var bd = input.usual ? usualBody(input) : body(input);
    var sx = input.sex === 'f' || input.sex === 'm' ? input.sex : 'x';
    var n = opts.samples || 600, r = rng(opts.seed || 7);
    var wins = {}; sizes.forEach(function (s) { wins[s] = 0; });
    var shape = MODEL[input.sex === 'f' ? 'f' : 'm'].shape;
    for (var i = 0; i < n; i++) {
      // correlated draw: a body's waist residual carries some chest with it, the way ANSUR says
      var s = {}, z = {};
      Object.keys(bd.mean).forEach(function (k) { z[k] = gauss(r); s[k] = bd.mean[k] + z[k] * bd.sd[k]; });
      if (!bd.fixed.chest) s.chest = bd.mean.chest + z.chest * bd.sd.chest * 0.85 + z.waist * bd.sd.waist * shape.belly.chest * 0.5;
      if (s.underbust != null && !bd.fixed.underbust) s.underbust = bd.mean.underbust + (s.chest - bd.mean.chest) * 0.8 + z.underbust * 2;
      var best = null, bestCost = Infinity;
      sizes.forEach(function (sz) {
        var c = score(fit, sizeRow(chart, sz), s, pref, sx, bd.src).cost;
        if (c < bestCost) { bestCost = c; best = sz; }
      });
      wins[best]++;
    }
    var ranked = sizes.slice().sort(function (a, b) { return wins[b] - wins[a]; });
    var top = ranked[0], second = ranked[1];
    var p1 = wins[top] / n, p2 = second ? wins[second] / n : 0;
    // at the mean body, how each measure reads in the recommended size
    var at = score(fit, sizeRow(chart, top), bd.mean, pref, sx, bd.src);
    var res = {
      size: top,
      confidence: p1,
      alt: p2 >= 0.25 ? second : null,
      altShare: p2,
      between: p2 >= 0.3,
      shares: sizes.map(function (sz) { return { size: sz, share: wins[sz] / n }; }),
      reads: at.reads,
      body: bd,
      edge: null
    };
    // the end of the range: everyone wins the smallest or largest size, so say it plainly
    var lo = score(fit, sizeRow(chart, sizes[sizes.length - 1]), bd.mean, pref, sx, bd.src);
    var hi = score(fit, sizeRow(chart, sizes[0]), bd.mean, pref, sx, bd.src);
    if (top === sizes[sizes.length - 1]) {
      var tight = Object.keys(lo.reads).filter(function (k) { var d = fit.dims[k]; return d.min != null && lo.reads[k].ease < d.min; });
      if (tight.length) res.edge = { side: 'over', dims: tight };
    }
    if (top === sizes[0]) {
      var loose = Object.keys(hi.reads).filter(function (k) { return hi.reads[k].off > 2.2; });
      if (loose.length) res.edge = { side: 'under', dims: loose };
    }
    if (stocked && stocked[top] === false) res.soldOut = true;
    return res;
  }

  function usualBody(input) {
    var sx = input.sex === 'f' ? 'f' : 'm';
    var u = USUAL[sx][input.usual];
    var est = input.heightCm && input.weightKg ? body(input) : body({ sex: sx, heightCm: sx === 'f' ? 165 : 178, weightKg: sx === 'f' ? 64 : 78 });
    if (!u) return est;
    est.mean.chest = u[0]; est.sd.chest = 3.5;
    est.mean.waist = u[1]; est.sd.waist = 4;
    if (u[2]) { est.mean.hip = u[2]; est.sd.hip = 3.5; }
    return est;
  }

  // Plain words for how a measure lands, from how far its room is from the drafted room.
  function word(read) {
    var x = read.off;
    if (x < -2.2) return 'tight';
    if (x < -1) return 'close';
    if (x <= 1) return 'as designed';
    if (x <= 2.2) return 'roomy';
    return 'very roomy';
  }

  // A jeans W number as the engine's waist (navel). Men's W sits about 2.5 cm under the body at the
  // navel. Women's W is close to the natural waist (high-street W28 = UK 10 = 70–71 cm), and the navel
  // is about 8 cm more. Until 2026-10-18 everyone got the men's rule: a W30 woman was sized into 72 cm.
  function jeansWaist(w, sex) { var c = w * 2.54; return sex === 'f' ? c + 8 : sex === 'm' ? c + 2.5 : c + 5; }

  var api = { jeansWaist: jeansWaist, MODEL: MODEL, body: body, recommend: recommend, score: score, word: word, USUAL: USUAL, predict: predict };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.FeralFit = api;
})(typeof window !== 'undefined' ? window : this);
/* FERAL FIT — the size guide on the product page. Needs FeralFit (the engine, above) and
   window.FERAL_FIT_V2 (assets/feral-fit-charts.js). One profile per shopper, kept in this
   browser, used on every product. Reads nothing from us and sends nothing to anyone but the
   cart: the answers ride on the order as attributes so returns can tell us which charts run
   large or small (the privacy line in the sheet says exactly that). */
(function () {
  'use strict';
  var F = window.FeralFit;
  if (!F || !window.FERAL_FIT_V2) return;
  var mounted = {};
  // A product page mounts its one chart from the snippet's config; the fit guide page mounts
  // any chart on demand (FeralFitMount({ chart: id }).open()), with no size buttons and no stock.
  function mount(CFG, opts) {
  opts = opts || {};
  if (mounted[CFG.chart]) return mounted[CFG.chart];
  var chart = window.FERAL_FIT_V2[CFG.chart];
  if (!chart) return null;
  var UID = 'ffit-' + CFG.chart.replace(/[^a-z0-9-]/gi, '');
  var hasFinder = !!(chart.fit && chart.fit.dims);
  var SIZES = chart.sizes.map(function (s) { return s.size; });
  // the product may spell a size differently from its chart ("2XL" for XXL, "Xs" for XS)
  function norm(v) { var u = String(v).trim().toUpperCase(); return u === '2XL' ? 'XXL' : u === '3XL' ? 'XXXL' : u; }
  var stock = {};
  (CFG.sizes || []).forEach(function (s) { stock[norm(s.size)] = s.available; });
  var KEY = 'feral.fit.v2';

  // ── profile ──────────────────────────────────────────────────────────────
  function readJSON(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function loadProfile() {
    var p = readJSON(KEY);
    if (p && p.v === 2) return p;
    var old = readJSON('feral.fit.profile.v1'); // the 2025-26 finder's profile: carry it over
    if (old && old.height_cm && old.weight_kg) {
      return { v: 2, sex: old.gender === 'female' ? 'f' : old.gender === 'male' ? 'm' : 'x', h: +old.height_cm, w: +old.weight_kg, migrated: true };
    }
    return { v: 2 };
  }
  function saveProfile() { try { localStorage.setItem(KEY, JSON.stringify(P)); } catch (e) {} }
  var P = loadProfile();
  var country = (CFG.country || 'GB').toUpperCase();
  if (!P.hu) P.hu = (country === 'GB' || country === 'IE' || country === 'US') ? 'ftin' : 'cm';
  if (!P.wu) P.wu = country === 'GB' || country === 'IE' ? 'st' : country === 'US' ? 'lb' : 'kg';
  P.pref = P.pref || {};
  var pref = P.pref[CFG.chart] || 'true';

  // ── helpers ──────────────────────────────────────────────────────────────
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.slice(0, 2) === 'on') n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null && attrs[k] !== false) n.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function r1(x) { return Math.round(x * 10) / 10; }
  function cmTxt(cm, unit) { return unit === 'in' ? r1(cm / 2.54) + '″' : Math.round(cm) + ' cm'; }
  function heightTxt(cm) {
    if (P.hu === 'cm') return Math.round(cm) + ' cm';
    var ti = Math.round(cm / 2.54), ft = Math.floor(ti / 12);
    return ft + '′' + (ti - ft * 12) + '″';
  }
  function weightTxt(kg) {
    if (P.wu === 'kg') return Math.round(kg) + ' kg';
    var lb = Math.round(kg / 0.45359237);
    if (P.wu === 'lb') return lb + ' lb';
    return Math.floor(lb / 14) + ' st ' + (lb % 14) + ' lb';
  }
  var DIM_LABEL = { chest: 'Chest', bust: 'Bust', waist: 'Waist', hip: 'Hips', length: 'Length', inseam: 'Leg', underbust: 'Band', rise: 'Rise' };

  // ── state → engine input ────────────────────────────────────────────────
  function input() {
    var i = { sex: P.sex || 'x', heightCm: P.h, weightKg: P.w, tummy: P.tummy || 0, hips: P.hips || 0, measured: {}, src: {}, measuredSd: {} };
    var m = P.m || {};
    ['chest', 'waist', 'hip'].forEach(function (k) { if (m[k] > 0) { i.measured[k] = m[k]; i.src[k] = 'tape'; } });
    function give(k, v, how, sd) { if (i.measured[k]) return; i.measured[k] = v; i.src[k] = how; i.measuredSd[k] = sd; }
    if (chart.ask && chart.ask.indexOf('bra') > -1 && P.bra && P.bra.band && P.bra.cup != null) {
      // UK bra: band ≈ underbust in inches; each cup letter one more inch of bust over the band.
      // The bra route has its own calibration (calibBy.bra), which absorbs how far that rule is off.
      give('underbust', P.bra.band * 2.54, 'bra', 2.5);
      give('chest', (P.bra.band + P.bra.cup) * 2.54, 'bra', 2.5);
    }
    if (chart.ask && chart.ask.indexOf('dress') > -1 && P.dress && F.USUAL.f[P.dress]) {
      // a UK dress size is a body: bust, natural waist, hip. The engine's waist sits at the navel,
      // about 8 cm below and wider than the natural waist on a woman (ANSUR II against UK body charts).
      // One dress size is about 5 cm of body, so it is a band of bodies, not a tape reading.
      var u = F.USUAL.f[P.dress];
      give('chest', u[0], 'dress', 3);
      give('waist', u[1] + 8, 'dress', 3.5);
      give('hip', u[2], 'dress', 3);
    }
    if (chart.ask && chart.ask.indexOf('jeans') > -1 && P.jeansW) {
      // a W number is a different body on a man and a woman (engine: jeansWaist)
      give('waist', F.jeansWaist(P.jeansW, P.sex), 'jeansW', 2.5);
    }
    return i;
  }
  function hwOk() { return P.h >= 130 && P.h <= 220 && P.w >= 35 && P.w <= 200; }
  // A garment with its own question (jeans W, bra size, dress size) gives no size until it is
  // answered or the shopper taps Not sure (stored as 0, or bra.none), so a shopper sized on a tee
  // is not handed a jeans or bikini size from height and weight alone. Answering either of the
  // bikini top's two is enough; an answer carries to every garment that asks the same question.
  // Harry, 2026-10-19: M on the tee came up as M on the jeans before any waist, "what about the bikinis".
  function answered(k) {
    if (k === 'jeans') return P.jeansW != null;
    if (k === 'dress') return P.dress != null;
    if (k === 'bra') return !!(P.bra && (P.bra.none || (P.bra.band && P.bra.cup != null)));
    return true;
  }
  function needsAsk() {
    var asks = chart.ask || [];
    if (!asks.length || asks.some(answered)) return null;
    return asks[0];
  }
  function ready() { return hwOk() && !needsAsk(); }
  var last = null;
  function compute() {
    if (!hasFinder || !ready()) { last = null; return null; }
    last = F.recommend(chart, input(), pref, { available: stock });
    return last;
  }

  // ── the bar on the product page ─────────────────────────────────────────
  var bar = opts.page === false ? null : document.querySelector('[data-ffit-bar]');
  var pillsRoot = (bar && bar.closest('[data-fp-is-size]')) || document.querySelector('[data-fp-is-size]') || document;
  function pillInput(size) {
    var list = pillsRoot.querySelectorAll('input[data-fp-pill]');
    for (var i = 0; i < list.length; i++) if (list[i].value === size) return list[i];
    for (var j = 0; j < list.length; j++) if (norm(list[j].value) === norm(size)) return list[j];
    return null;
  }
  function markPill(size) {
    pillsRoot.querySelectorAll('.fp__pill.ffit-mine').forEach(function (l) { l.classList.remove('ffit-mine'); });
    var inp = size && pillInput(size);
    if (inp && inp.closest('.fp__pill')) inp.closest('.fp__pill').classList.add('ffit-mine');
    // the quick size sheet behind the sticky Add to bag says it too
    document.querySelectorAll('.ffit-mine-tag').forEach(function (t) { t.remove(); });
    if (!size) return;
    document.querySelectorAll('[data-fp-sheet-row]').forEach(function (row) {
      if (norm(row.getAttribute('data-value')) !== norm(size)) return;
      var name = row.querySelector('.fp__sheet-row-name');
      if (name) name.appendChild(el('span', { class: 'ffit-mine-tag', text: 'Your size' }));
    });
  }
  function selectSize(size) {
    var inp = pillInput(size);
    if (!inp) return false;
    inp.checked = true;
    inp.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  var ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8.5 8.5 3 21 15.5 15.5 21z"/><path d="m7 7 1.5 1.5M10 5.5 12 7.5M9.5 10.5 11 12M13 9l2 2M12.5 13.5 14 15M16 12l2 2"/></svg>';
  function paintBar() {
    if (!bar) return;
    if (!hasFinder) {
      // table-only garments (Andie's call: stretch and tie-side pieces rarely come back for size)
      bar.setAttribute('data-ffit-open', 'chart');
      bar.innerHTML = '<span class="ffit__bar-icon">' + ICON + '</span><span class="ffit__bar-text"><span class="ffit__bar-title">Size chart</span>' +
        '<span class="ffit__bar-sub">Measurements for every size</span></span><span class="ffit__bar-go">Open</span>';
      bar.setAttribute('aria-label', 'Open the size chart');
      return;
    }
    var r = compute();
    var need = !r && hwOk() && needsAsk();
    if (need) {
      var ab = ASK_BAR[need];
      bar.innerHTML = '<span class="ffit__bar-icon">' + ICON + '</span><span class="ffit__bar-text"><span class="ffit__bar-title">' + ab[0] + '</span>' +
        '<span class="ffit__bar-sub">' + ab[1] + '</span></span><span class="ffit__bar-go">Start</span>';
      bar.setAttribute('aria-label', ab[0] + ': ' + ab[1]);
      markPill(null);
      return;
    }
    if (!r) {
      bar.innerHTML = '<span class="ffit__bar-icon">' + ICON + '</span><span class="ffit__bar-text"><span class="ffit__bar-title">Find your size</span>' +
        '<span class="ffit__bar-sub">Height and weight, 20 seconds</span></span><span class="ffit__bar-go">Start</span>';
      bar.setAttribute('aria-label', 'Find your size: answer two questions');
      markPill(null);
      return;
    }
    var line = r.between ? 'Between ' + order(r.size, r.alt).join(' and ') : matchWord(r) + ' for ' + heightTxt(P.h) + ', ' + weightTxt(P.w);
    if (r.soldOut) { var nx = nearestInStock(r.size); line = r.size + ' is sold out' + (nx ? ', ' + nx + ' is the next best' : ''); }
    // a garment with a better clue than height and weight (jeans W, bra size, dress size) asks for it
    // here, so a returning shopper sized from another product is told the answer can be sharper
    var cl = !r.soldOut && clue();
    if (cl) line = 'Sharper with your ' + CLUE_WORD[cl];
    bar.innerHTML = '<span class="ffit__bar-icon">' + ICON + '</span><span class="ffit__bar-text"><span class="ffit__bar-title">Your size <span class="ffit__bar-size">' + esc(r.size) + '</span></span>' +
      '<span class="ffit__bar-sub">' + esc(line) + '</span></span><span class="ffit__bar-go">' + (cl ? 'Sharpen' : 'Why') + '</span>';
    bar.setAttribute('aria-label', 'Your size is ' + r.size + '. ' + line + '. Open to see why or change your answers.');
    markPill(r.size);
    remember(r, 'bar');
  }
  var ASK_BAR = { jeans: ['Find your jeans size', 'Your usual W size, one tap'], bra: ['Find your size', 'Your bra size, one tap'], dress: ['Find your size', 'Your usual dress size, one tap'] };
  var ASK_WAIT = { jeans: 'Pick your W size above', bra: 'Pick your bra or dress size above', dress: 'Pick your dress size above' };
  var CLUE_WORD = { jeans: 'W size', bra: 'bra size', dress: 'dress size' };
  // the most useful answer this garment asks for that the shopper has not given
  function clue() {
    var asks = chart.ask || [];
    if (asks.indexOf('jeans') > -1 && !P.jeansW) return 'jeans';
    if (P.sex === 'm') return null;
    if (asks.indexOf('bra') > -1 && !(P.bra && P.bra.band && P.bra.cup != null) && !P.dress) return 'bra';
    if (asks.indexOf('dress') > -1 && !P.dress && !(asks.indexOf('bra') > -1 && P.bra && P.bra.band)) return P.sex === 'f' ? 'dress' : null;
    return null;
  }
  function order(a, b) {
    return SIZES.indexOf(a) < SIZES.indexOf(b) ? [a, b] : [b, a];
  }
  function matchWord(r) {
    return r.confidence >= 0.7 ? 'Strong match' : r.confidence >= 0.5 ? 'Good match' : 'Best match';
  }

  // ── the sheet ────────────────────────────────────────────────────────────
  var sheet, panel, bodyEl, tabs = {}, panes = {}, resultEl, dockEl, lastFocus, built = false;
  function build() {
    if (built) return; built = true;
    sheet = el('div', { class: 'ffit-sheet', hidden: true, role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': UID + '-title' });
    var scrim = el('div', { class: 'ffit-sheet__scrim', onclick: close });
    panel = el('div', { class: 'ffit-sheet__panel' });
    var head = el('div', { class: 'ffit-sheet__head' }, [
      el('div', { class: 'ffit-sheet__titles' }, [
        el('p', { class: 'ffit-sheet__eyebrow', text: 'Size & fit' }),
        el('h2', { class: 'ffit-sheet__title', id: UID + '-title', tabindex: '-1', text: chart.title || CFG.title })
      ]),
      el('button', { class: 'ffit-sheet__close', type: 'button', 'aria-label': 'Close', onclick: close,
        html: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M3 3l10 10M13 3 3 13"/></svg>' })
    ]);
    var tabBar = el('div', { class: 'ffit-tabs', role: 'tablist' });
    var names = hasFinder ? [['find', 'Your size'], ['chart', 'Size chart'], ['measure', 'How to measure']] : [['chart', 'Size chart'], ['measure', 'How to measure']];
    names.forEach(function (n) {
      var t = el('button', { class: 'ffit-tab', type: 'button', role: 'tab', id: UID + '-tab-' + n[0], 'aria-controls': UID + '-pane-' + n[0], 'aria-selected': 'false', text: n[1], onclick: function () { show(n[0]); } });
      tabs[n[0]] = t; tabBar.appendChild(t);
    });
    bodyEl = el('div', { class: 'ffit-sheet__body' });
    names.forEach(function (n) {
      panes[n[0]] = el('div', { class: 'ffit-pane', id: UID + '-pane-' + n[0], role: 'tabpanel', 'aria-labelledby': UID + '-tab-' + n[0], hidden: true });
      bodyEl.appendChild(panes[n[0]]);
    });
    panel.appendChild(el('div', { class: 'ffit-sheet__grab', 'aria-hidden': 'true' }));
    panel.appendChild(head); panel.appendChild(tabBar); panel.appendChild(bodyEl);
    sheet.appendChild(scrim); sheet.appendChild(panel);
    document.body.appendChild(sheet);
    if (hasFinder) buildFind();
    buildChart();
    buildMeasure();
    sheet.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'Tab') trap(e);
    });
  }
  function trap(e) {
    var f = Array.prototype.filter.call(panel.querySelectorAll('button, input, a[href], summary, [tabindex="0"]'), function (n) { return !n.disabled && n.offsetParent !== null; });
    if (!f.length) return;
    var a = f[0], z = f[f.length - 1];
    if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
    else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
  }
  function show(name) {
    Object.keys(panes).forEach(function (k) {
      panes[k].hidden = k !== name;
      tabs[k].setAttribute('aria-selected', k === name ? 'true' : 'false');
      tabs[k].tabIndex = k === name ? 0 : -1;
    });
    if (name === 'chart') paintChart();
    bodyEl.scrollTop = 0;
  }
  function open(name) {
    build();
    lastFocus = document.activeElement;
    show(name && panes[name] ? name : hasFinder ? 'find' : 'chart');
    sheet.hidden = false;
    document.documentElement.classList.add('ffit-lock');
    requestAnimationFrame(function () { sheet.classList.add('is-open'); });
    // focus the title, not the first answer: a ring on "Woman" reads as a choice nobody made
    setTimeout(function () { panel.querySelector('.ffit-sheet__title').focus({ preventScroll: true }); }, 60);
    track('open_' + (name || 'find'));
  }
  function close() {
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('ffit-lock');
    setTimeout(function () { sheet.hidden = true; }, 220);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  // ── "Your size" pane ─────────────────────────────────────────────────────
  var hIn = {}, wIn = {}, errEl;
  function seg(options, current, onPick, small) {
    var s = el('div', { class: 'ffit-seg' + (small ? ' ffit-seg--small' : ''), role: 'group' });
    options.forEach(function (o) {
      s.appendChild(el('button', { type: 'button', 'aria-pressed': o[0] === current ? 'true' : 'false', text: o[1], onclick: function () {
        s.querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        this.setAttribute('aria-pressed', 'true');
        onPick(o[0]);
      } }));
    });
    return s;
  }
  function chips(list, current, onPick, label) {
    var c = el('div', { class: 'ffit-chips', role: 'group', 'aria-label': label, style: '--n:' + list.length });
    list.forEach(function (o) {
      c.appendChild(el('button', { type: 'button', class: 'ffit-chip', 'aria-pressed': String(o[0]) === String(current) ? 'true' : 'false',
        html: '<b>' + esc(o[1]) + '</b>' + (o[2] ? '<small>' + esc(o[2]) + '</small>' : ''),
        onclick: function () {
          c.querySelectorAll('.ffit-chip').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          this.setAttribute('aria-pressed', 'true');
          onPick(o[0]);
        } }));
    });
    return c;
  }
  function field(unit, value, ph, onInput, label) {
    var inp = el('input', { type: 'number', inputmode: 'decimal', step: 'any', min: '0', placeholder: ph, 'aria-label': label, value: value == null ? '' : value });
    inp.addEventListener('input', function () { onInput(inp.value); });
    return { wrap: el('label', { class: 'ffit-field' }, [inp, el('span', { text: unit })]), input: inp };
  }
  function q(label, hint, right, content) {
    return el('div', { class: 'ffit-q' }, [
      el('div', { class: 'ffit-q__head' }, [el('p', { class: 'ffit-q__label', text: label }), right || null]),
      hint ? el('p', { class: 'ffit-q__hint', text: hint }) : null
    ].concat(content));
  }
  function heightFields(wrap) {
    wrap.innerHTML = '';
    if (P.hu === 'cm') {
      var c = field('cm', P.h ? Math.round(P.h) : '', '175', function (v) { P.h = +v || null; changed(); }, 'Height in centimetres');
      wrap.appendChild(c.wrap); hIn = { cm: c.input };
    } else {
      var ti = P.h ? Math.round(P.h / 2.54) : null;
      var ft = field('ft', ti ? Math.floor(ti / 12) : '', '5', fromFtIn, 'Height, feet');
      var inch = field('in', ti ? ti % 12 : '', '9', fromFtIn, 'Height, inches');
      wrap.appendChild(ft.wrap); wrap.appendChild(inch.wrap); hIn = { ft: ft.input, inch: inch.input };
    }
  }
  function fromFtIn() {
    var f = +hIn.ft.value || 0, i = +hIn.inch.value || 0;
    P.h = f > 0 ? (f * 12 + i) * 2.54 : null; changed();
  }
  function weightFields(wrap) {
    wrap.innerHTML = '';
    if (P.wu === 'st') {
      var lb = P.w ? Math.round(P.w / 0.45359237) : null;
      var st = field('st', lb ? Math.floor(lb / 14) : '', '11', fromSt, 'Weight, stone');
      var l = field('lb', lb ? lb % 14 : '', '6', fromSt, 'Weight, pounds');
      wrap.appendChild(st.wrap); wrap.appendChild(l.wrap); wIn = { st: st.input, lb: l.input };
    } else {
      var isKg = P.wu === 'kg';
      var v = P.w ? Math.round(isKg ? P.w : P.w / 0.45359237) : '';
      var one = field(isKg ? 'kg' : 'lb', v, isKg ? '70' : '160', function (val) { P.w = +val ? (isKg ? +val : +val * 0.45359237) : null; changed(); }, 'Weight in ' + (isKg ? 'kilograms' : 'pounds'));
      wrap.appendChild(one.wrap); wIn = { one: one.input };
    }
  }
  function fromSt() {
    var s = +wIn.st.value || 0, l = +wIn.lb.value || 0;
    P.w = s > 0 ? (s * 14 + l) * 0.45359237 : null; changed();
  }
  function buildFind() {
    var p = panes.find;
    var asks = chart.ask || [];
    var dims = Object.keys(chart.fit.dims);
    p.appendChild(q('Size me as', 'Only used to estimate your measurements.', null, [
      chips([['f', 'Woman'], ['m', 'Man'], ['x', 'Skip']], P.sex || '', function (v) { P.sex = v; changed(); }, 'Size me as')
    ]));
    var hWrap = el('div', { class: 'ffit-num' });
    heightFields(hWrap);
    p.appendChild(q('Height', null, seg([['cm', 'cm'], ['ftin', 'ft in']], P.hu, function (u) { P.hu = u; heightFields(hWrap); saveProfile(); }, true), [hWrap]));
    var wWrap = el('div', { class: 'ffit-num' });
    weightFields(wWrap);
    p.appendChild(q('Weight', null, seg([['kg', 'kg'], ['st', 'st'], ['lb', 'lb']], P.wu, function (u) { P.wu = u; weightFields(wWrap); saveProfile(); }, true), [wWrap]));
    var prefs = chart.prefs || { closer: { label: 'Closer' }, 'true': { label: 'As shown' }, oversized: { label: 'Roomier' } };
    var prefList = Object.keys(prefs).map(function (k) { return [k, prefs[k].label, prefs[k].desc]; });
    p.appendChild(q('How do you want it to fit?', null, null, [chips(prefList, pref, function (v) { pref = v; P.pref[CFG.chart] = v; changed(); }, 'Fit')]));
    if (asks.indexOf('dress') > -1) {
      var dl = [4, 6, 8, 10, 12, 14, 16, 18, 20].map(function (d) { return [d, String(d)]; });
      dl.push([0, 'Not sure, use my height and weight']);
      var dc = chips(dl, P.dress != null ? P.dress : '', function (v) { P.dress = +v; changed(); }, 'UK dress size');
      dc.style.setProperty('--n', 5);
      dc.lastChild.classList.add('ffit-chip--wide');
      p.appendChild(q('Your usual UK dress size', 'The size you usually buy. It sharpens your size here.', null, [dc]));
    }
    if (asks.indexOf('bra') > -1) {
      var bra = P.bra || {};
      var bands = [28, 30, 32, 34, 36, 38, 40, 42];
      var cups = [['0', 'AA'], ['1', 'A'], ['2', 'B'], ['3', 'C'], ['4', 'D'], ['5', 'DD'], ['6', 'E'], ['7', 'F'], ['8', 'FF'], ['9', 'G']];
      // a band or cup clears Not sure, and Not sure clears the band and cup
      function braPick(k, v) { P.bra = P.bra || {}; delete P.bra.none; P.bra[k] = +v; noC.firstChild.setAttribute('aria-pressed', 'false'); changed(); }
      var bandC = chips(bands.map(function (b) { return [b, String(b)]; }), bra.band || '', function (v) { braPick('band', v); }, 'Bra band');
      bandC.style.setProperty('--n', 4);
      var cupC = chips(cups, bra.cup != null ? String(bra.cup) : '', function (v) { braPick('cup', v); }, 'Bra cup');
      cupC.style.setProperty('--n', 5);
      var noC = chips([[1, 'Not sure, use my height and weight']], bra.none ? 1 : '', function () {
        P.bra = { none: true };
        [bandC, cupC].forEach(function (g) { g.querySelectorAll('.ffit-chip').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); }); });
        changed();
      }, 'Not sure of bra size');
      noC.style.setProperty('--n', 1);
      p.appendChild(q('Your bra size', 'UK sizing. The most accurate answer for this top.', null, [bandC, cupC, noC]));
    }
    if (asks.indexOf('jeans') > -1) {
      var ws = []; for (var w = 24; w <= 40; w++) ws.push([w, 'W' + w]);
      ws.push([0, 'Not sure, use my height and weight']);
      var jc = chips(ws, P.jeansW != null ? P.jeansW : '', function (v) { P.jeansW = +v; changed(); }, 'Jeans waist');
      jc.style.setProperty('--n', 6);
      jc.lastChild.classList.add('ffit-chip--wide');
      p.appendChild(q('Your usual jeans waist', 'The W number on jeans that fit you elsewhere. It decides your size here.', null, [jc]));
    }
    // closer answers, folded
    var more = el('details', { class: 'ffit-more' });
    more.appendChild(el('summary', { html: '<span>Get a closer fit<small>optional</small></span>' }));
    if (dims.some(function (d) { return d === 'chest' || d === 'waist'; })) {
      more.appendChild(q('Your tummy', null, null, [chips([[-1, 'Flatter'], [0, 'Average'], [1, 'Rounder']], P.tummy || 0, function (v) { P.tummy = +v; changed(); }, 'Tummy')]));
    }
    if (dims.indexOf('hip') > -1 || P.sex === 'f') {
      more.appendChild(q('Your hips', null, null, [chips([[-1, 'Narrower'], [0, 'Average'], [1, 'Curvier']], P.hips || 0, function (v) { P.hips = +v; changed(); }, 'Hips')]));
    }
    var mWrap = el('div', { class: 'ffit-num' });
    var mUnit = P.hu === 'cm' ? 'cm' : 'in';
    var mm = P.m || {};
    var want = dims.filter(function (d) { return d === 'chest' || d === 'waist' || d === 'hip'; });
    want.forEach(function (d) {
      var f = field(mUnit, mm[d] ? Math.round(mUnit === 'in' ? mm[d] / 2.54 : mm[d]) : '', DIM_LABEL[d], function (v) {
        P.m = P.m || {}; P.m[d] = +v ? (mUnit === 'in' ? +v * 2.54 : +v) : null; changed();
      }, DIM_LABEL[d] + ' measurement in ' + mUnit);
      mWrap.appendChild(f.wrap);
    });
    if (want.length) more.appendChild(q('Your measurements', 'Measure your body, not a garment. Any you enter replace our estimate.', null, [mWrap]));
    p.appendChild(more);
    errEl = el('p', { class: 'ffit-err', hidden: true, role: 'status' });
    p.appendChild(errEl);
    resultEl = el('div', { class: 'ffit-result', hidden: true, 'aria-live': 'polite' });
    p.appendChild(resultEl);
    p.appendChild(el('p', { class: 'ffit-small', text: 'Your answers stay in this browser so every product shows your size. When you order, they are saved with the order so we can tell which sizes run large or small. Nothing is shared with anyone else.' }));
    dockEl = el('div', { class: 'ffit-dock', hidden: true });
    p.appendChild(dockEl);
    bodyEl.addEventListener('scroll', dockVisibility, { passive: true });
    paintResult();
  }
  var tmr;
  function changed() {
    saveProfile();
    clearTimeout(tmr);
    tmr = setTimeout(function () { paintResult(); paintBar(); }, 120);
  }
  function verdictLine(r) {
    if (r.edge && r.edge.side === 'over') return 'Our largest size, and it will sit closer than designed.';
    if (r.edge && r.edge.side === 'under') return 'Our smallest size, and it will still be roomy on you.';
    if (r.between) {
      var pair = order(r.size, r.alt);
      return 'Between ' + pair[0] + ' and ' + pair[1] + '. ' + r.size + ' is the closer call.';
    }
    return matchWord(r) + '.';
  }
  function betweenWhy(r) {
    var pair = order(r.size, r.alt);
    var shown = chart.prefs && chart.prefs['true'] ? chart.prefs['true'].label.toLowerCase() : 'as shown';
    return pair[0] + ' for a closer fit, ' + pair[1] + ' if you like room. Pick the one nearer the look you want.';
  }
  function readWords(k, rd) {
    var d = chart.fit.dims[k];
    if (d.range) return rd.off < -1 ? 'at the top of this size' : rd.off > 1 ? 'at the bottom of this size' : 'right in this size';
    if (d.say) {
      for (var i = 0; i < d.say.length; i++) if (rd.ease < d.say[i][0]) return d.say[i][1];
      return d.say[d.say.length - 1][1];
    }
    return F.word(rd);
  }
  function readDetail(k, rd) {
    var d = chart.fit.dims[k];
    if (d.say || d.range) return '';
    var unit = P.hu === 'cm' ? 'cm' : 'in';
    var room = rd.ease;
    if (Math.abs(room) < 1) return 'skims the body';
    if (room < 0) return cmTxt(-room, unit) + ' of stretch';
    return cmTxt(room, unit) + ' of room';
  }
  function paintResult() {
    if (!resultEl) return;
    var r = compute();
    var bad = (P.h && (P.h < 130 || P.h > 220)) ? 'That height looks off. Check the units.' :
              (P.w && (P.w < 35 || P.w > 200)) ? 'That weight looks off. Check the units.' : '';
    errEl.textContent = bad; errEl.hidden = !bad;
    var need = !r && hwOk() && needsAsk();
    if (need) {
      resultEl.innerHTML = '<p class="ffit-note"><b>' + ASK_WAIT[need] + '</b> to see your size here, or tap Not sure.</p>';
      resultEl.hidden = false; dockEl.hidden = true; return;
    }
    if (!r) { resultEl.hidden = true; dockEl.hidden = true; return; }
    var html = '';
    html += '<div class="ffit-result__top"><p class="ffit-result__size" aria-hidden="true">' + esc(r.size) + '</p><div class="ffit-result__say">' +
      '<p class="ffit-result__kicker">Your size</p><p class="ffit-result__line"><span class="ffit-sr">Your size is ' + esc(r.size) + '. </span>' + esc(verdictLine(r)) + '</p>' +
      (r.between ? '<p class="ffit-result__why">' + esc(betweenWhy(r)) + '</p>' : '') + '</div></div>';
    // how sure: of 100 people with these answers, how many fit best in each size
    var shown = r.shares.filter(function (s) { return s.share >= 0.005; });
    var lo = SIZES.indexOf(shown[0].size), hi = SIZES.indexOf(shown[shown.length - 1].size);
    lo = Math.max(0, lo - 1); hi = Math.min(SIZES.length - 1, hi + 1);
    var cols = r.shares.slice(lo, hi + 1);
    html += '<div class="ffit-odds" style="--n:' + cols.length + '" role="img" aria-label="' + esc(cols.map(function (c) { return Math.round(c.share * 100) + ' in ' + c.size; }).join(', ')) + '">';
    cols.forEach(function (c) {
      html += '<div class="ffit-odds__col' + (c.size === r.size ? ' is-top' : '') + '"><div class="ffit-odds__bar"><div class="ffit-odds__fill" style="height:' + Math.max(c.share > 0 ? 6 : 0, Math.round(c.share * 100)) + '%"></div></div>' +
        '<span class="ffit-odds__lab">' + esc(c.size) + '</span></div>';
    });
    html += '</div>';
    var named = cols.filter(function (c) { return c.share >= 0.05; }).sort(function (a, b) { return b.share - a.share; });
    html += '<p class="ffit-odds__cap">Of 100 people with your answers, ' + named.map(function (c, i) {
      return Math.round(c.share * 100) + (i === 0 ? ' fit best in ' : ' in ') + c.size;
    }).join(', ') + '.</p>';
    // how each measure lands in that size
    var keys = Object.keys(r.reads).filter(function (k) { return chart.fit.dims[k].show !== false; });
    if (keys.length) {
      html += '<dl class="ffit-reads">';
      var firstScale = true;
      keys.forEach(function (k) {
        var rd = r.reads[k];
        var pos = Math.max(4, Math.min(96, 50 + rd.off * 14));
        var det = readDetail(k, rd);
        html += '<div class="ffit-read"><dt>' + esc(chart.fit.dims[k].label || DIM_LABEL[k] || k) + '</dt><dd><span class="ffit-read__words">' + esc(cap(readWords(k, rd))) + (det ? '<small>' + esc(det) + '</small>' : '') + '</span>' +
          (chart.fit.dims[k].say || chart.fit.dims[k].range ? '' : '<span class="ffit-scale" aria-hidden="true"><i style="left:' + pos + '%"></i></span>' +
            (firstScale ? '<span class="ffit-scale-key" aria-hidden="true"><span>Tighter</span><span>As designed</span><span>Roomier</span></span>' : '')) + '</dd></div>';
        if (!chart.fit.dims[k].say && !chart.fit.dims[k].range) firstScale = false;
      });
      html += '</dl>';
    }
    var notes = (chart.notes || []).filter(function (n) { return noteApplies(n, r); }).map(function (n) { return n.text; });
    if (r.soldOut) {
      var next = nearestInStock(r.size);
      notes.unshift('<b>' + esc(r.size) + ' is sold out right now.</b> ' + (next ? esc(next) + ' is in stock and ' + (SIZES.indexOf(next) > SIZES.indexOf(r.size) ? 'a little roomier.' : 'a little closer.') : 'Ask to be told when it is back.'));
    }
    notes.forEach(function (n) { html += '<p class="ffit-note">' + n + '</p>'; });
    if (opts.page !== false) html += '<div class="ffit-cta">';
    if (opts.page === false) { /* the fit guide page has no product to choose a size on */ }
    else if (r.soldOut) {
      var nx = nearestInStock(r.size);
      if (nx) html += '<button type="button" class="ffit-btn" data-ffit-pick="' + esc(nx) + '">Choose ' + esc(nx) + '</button>';
      html += '<button type="button" class="ffit-btn ffit-btn--ghost" data-ffit-pick="' + esc(r.size) + '">Tell me when ' + esc(r.size) + ' is back</button>';
    } else {
      html += '<button type="button" class="ffit-btn" data-ffit-pick="' + esc(r.size) + '">Choose ' + esc(r.size) + '</button>';
      if (r.between && stock[r.alt] !== false) html += '<button type="button" class="ffit-btn ffit-btn--ghost" data-ffit-pick="' + esc(r.alt) + '">Choose ' + esc(r.alt) + '</button>';
    }
    if (opts.page !== false) html += '</div>';
    if (chart.model) html += '<p class="ffit-modelnote">' + esc(chart.model) + '</p>';
    resultEl.innerHTML = html;
    resultEl.hidden = false;
    resultEl.querySelectorAll('[data-ffit-pick]').forEach(function (b) {
      b.addEventListener('click', function () { pick(b.getAttribute('data-ffit-pick')); });
    });
    dockEl.innerHTML = '<span class="ffit-dock__size">' + esc(r.size) + '</span><span class="ffit-dock__txt"><b>Your size</b>' + esc(r.between ? 'Between ' + order(r.size, r.alt).join(' and ') : matchWord(r)) + '</span>' +
      '<button type="button" class="ffit-btn" data-ffit-see>See why</button>';
    dockEl.querySelector('[data-ffit-see]').addEventListener('click', function () { resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    dockVisibility();
    remember(r, 'sheet');
    track('result', r);
  }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function dockVisibility() {
    if (!dockEl || !resultEl || resultEl.hidden) { if (dockEl) dockEl.hidden = true; return; }
    var b = bodyEl.getBoundingClientRect(), rr = resultEl.getBoundingClientRect();
    dockEl.hidden = rr.top < b.bottom - 120;
  }
  // the in-stock size nearest the one we chose, and of two equally near, the one more bodies fit
  function nearestInStock(size) {
    var i = SIZES.indexOf(size), share = {};
    if (last) last.shares.forEach(function (s) { share[s.size] = s.share; });
    for (var d = 1; d < SIZES.length; d++) {
      var up = SIZES[i + d], dn = SIZES[i - d];
      var okU = up && stock[up], okD = dn && stock[dn];
      if (okU && okD) return (share[dn] || 0) > (share[up] || 0) ? dn : up;
      if (okU) return up;
      if (okD) return dn;
    }
    return null;
  }
  function noteApplies(n, r) {
    if (!n.when) return true;
    var w = n.when;
    if (w.sizes && w.sizes.indexOf(r.size) < 0) return false;
    if (w.minHeight && !(P.h >= w.minHeight)) return false;
    if (w.maxHeight && !(P.h <= w.maxHeight)) return false;
    if (w.edge && !(r.edge && r.edge.side === w.edge)) return false;
    return true;
  }
  function pick(size) {
    var ok = selectSize(size);
    remember(last, 'pick', size);
    track('pick', { size: size });
    close();
    if (!ok) return;
    if (stock[size] === false) {
      var cta = document.querySelector('[data-fp-cta], .fp__cta');
      if (cta) cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ── chart pane ───────────────────────────────────────────────────────────
  var unit = P.hu === 'cm' ? 'cm' : 'in';
  if (country !== 'US' && country !== 'GB' && country !== 'IE') unit = 'cm';
  function buildChart() {
    var p = panes.chart;
    if (chart.intro) p.appendChild(el('p', { class: 'ffit-chart__intro', text: chart.intro }));
    var tools = el('div', { class: 'ffit-chart__tools' }, [
      el('small', { text: 'Garment measured laid flat' }),
      seg([['cm', 'cm'], ['in', 'in']], unit, function (u) { unit = u; paintChart(); }, true)
    ]);
    p.appendChild(tools);
    p.appendChild(el('div', { class: 'ffit-table-wrap', tabindex: '0', role: 'region', 'aria-label': 'Size chart' }, [el('table', { class: 'ffit-table' })]));
    p.appendChild(el('p', { class: 'ffit-chart__key' }));
    if (chart.diagram) p.appendChild(el('figure', { class: 'ffit-diagram' }, [el('img', { src: chart.diagram, alt: 'Where each measurement is taken on the garment', loading: 'lazy', width: '660', height: '660' })]));
  }
  function paintChart() {
    var p = panes.chart; if (!p) return;
    var t = p.querySelector('.ffit-table');
    var mine = last && last.size;
    var h = '<thead><tr><th scope="col">Size</th>';
    chart.columns.forEach(function (c) { h += '<th scope="col">' + esc(c.label) + (c.sub ? '<small>' + esc(c.sub) + '</small>' : '') + '</th>'; });
    h += '</tr></thead><tbody>';
    chart.sizes.forEach(function (s) {
      var cls = (s.size === mine ? 'is-mine' : '') + (stock[s.size] === false ? ' is-out' : '');
      h += '<tr class="' + cls.trim() + '"><th scope="row">' + esc(s.size) + '</th>';
      chart.columns.forEach(function (c) {
        var v = s[c.key];
        var out;
        if (v == null || v === '') out = '–';
        else if (typeof v === 'number' && c.unit !== 'none') out = unit === 'in' ? r1(v / 2.54) : r1(v);
        else if (c.unit === 'cm-range' && unit === 'in') out = esc(String(v).replace(/\d+(\.\d+)?/g, function (n) { return r1(+n / 2.54); }));
        else out = esc(v);
        h += '<td>' + out + '</td>';
      });
      h += '</tr>';
    });
    t.innerHTML = h + '</tbody>';
    var key = [];
    if (mine) key.push('Your size is marked.');
    if (SIZES.some(function (s) { return stock[s] === false; })) key.push('Greyed sizes are sold out.');
    key.push('Measurements in ' + (unit === 'in' ? 'inches' : 'centimetres') + '. ' + (chart.tolerance || 'Allow 1 to 2 cm either way; each piece is cut and sewn by hand.'));
    p.querySelector('.ffit-chart__key').textContent = key.join(' ');
  }

  // ── how to measure ───────────────────────────────────────────────────────
  var HOW = {
    chest: ['Chest', 'Arms down, tape under your armpits round the fullest part of your chest. Keep it level and snug, not tight.'],
    bust: ['Bust', 'Round the fullest part of your bust, wearing a bra without padding. Keep the tape level across your back.'],
    underbust: ['Under the bust', 'Round your ribcage directly under your bust, snug. This is your band.'],
    waist: ['Waist', 'Where your trousers sit, usually just under the belly button. Breathe out, then measure.'],
    hip: ['Hips', 'Feet together, round the fullest part of your hips and seat.'],
    inseam: ['Inside leg', 'From the crotch down the inside of your leg to the floor, standing straight without shoes. Easiest with a pair of trousers that fit you.'],
    length: ['Your length', 'Take a top that fits you well, lay it flat and measure from the highest point of the shoulder down to the hem. Compare it with Length in the chart.']
  };
  function buildMeasure() {
    var p = panes.measure;
    var keys = (chart.measure || (chart.fit ? Object.keys(chart.fit.dims) : ['chest', 'length'])).filter(function (k) { return HOW[k]; });
    var ul = el('ul', { class: 'ffit-howto' });
    keys.forEach(function (k) { ul.appendChild(el('li', null, [el('b', { text: HOW[k][0] }), el('span', { text: HOW[k][1] })])); });
    ul.appendChild(el('li', null, [el('b', { text: 'Quickest of all' }), el('span', { text: 'Lay something you own and love the fit of flat, measure it the same way, and pick the size in the chart with the closest numbers.' })]));
    p.appendChild(ul);
  }

  // ── what we keep: on the cart, for the order ────────────────────────────
  // Every size this browser was shown, one entry per chart, so an order with a tee AND jeans
  // carries the size shown for each (the old single _fsg_last_* fields kept only the last product
  // looked at). The tracker (~/feral-fit/tracker) joins each line to its chart's entry and to what
  // came back. Written whenever a size is shown, not only when "Choose" is pressed: most shoppers
  // with saved answers tap the pill themselves.
  var RECS = 'feral.fit.recs';
  function remember(r, how, pickSize) {
    if (!r || opts.page === false) return;
    var all = readJSON(RECS) || {}, now = Date.now();
    Object.keys(all).forEach(function (k) { if (!(all[k] && now - all[k].t < 14 * 864e5)) delete all[k]; });
    var e = { r: r.size, c: Math.round(r.confidence * 100), p: pref, h: CFG.handle, k: how, t: now };
    if (r.alt && r.between) e.a = r.alt;
    var via = [];
    var src = input().src;
    Object.keys(src).forEach(function (d) { if (via.indexOf(src[d]) < 0) via.push(src[d]); });
    if (via.length) e.v = via.join('+');
    var prev = all[CFG.chart];
    if (pickSize) e.pk = pickSize; else if (prev && prev.pk && prev.r === r.size) e.pk = prev.pk;
    all[CFG.chart] = e;
    var keys = Object.keys(all).sort(function (a, b) { return all[b].t - all[a].t; });
    keys.slice(12).forEach(function (k) { delete all[k]; });
    try { localStorage.setItem(RECS, JSON.stringify(all)); } catch (x) {}
    writeCart(pickSize || null, all);
  }
  var lastSent = '';
  function writeCart(size, all) {
    if (!last) return;
    var snap = { v: 2, sex: P.sex || 'x', height_cm: Math.round(P.h), weight_kg: Math.round(P.w), fit: pref, tummy: P.tummy || 0, hips: P.hips || 0 };
    if (P.m) Object.keys(P.m).forEach(function (k) { if (P.m[k]) snap['m_' + k] = Math.round(P.m[k]); });
    if (P.bra && P.bra.band) snap.bra = P.bra.band + ':' + P.bra.cup;
    if (P.jeansW) snap.jeans_w = P.jeansW;
    if (P.dress) snap.dress = P.dress;
    // the per-chart map without its timestamps: the order date is enough
    var recs = {};
    Object.keys(all || {}).forEach(function (k) { var e = Object.assign({}, all[k]); delete e.t; recs[k] = e; });
    var attrs = {
      '_fsg_used': 'true',
      '_fsg_profile': JSON.stringify(snap),
      '_fsg_recs': JSON.stringify(recs),
      '_fsg_last_recommendation': last.size,
      '_fsg_last_confidence': String(Math.round(last.confidence * 100)),
      '_fsg_last_pick': size || '',
      '_fsg_last_product': CFG.handle,
      '_fsg_last_chart': CFG.chart,
      '_fsg_engine': 'feral-fit-2',
      '_fsg_capture': '3'
    };
    var body = JSON.stringify({ attributes: attrs });
    // once the answers settle (typing a height repaints the bar on every key), and a pick at once
    clearTimeout(cartTmr);
    cartTmr = setTimeout(function () {
      if (body === lastSent) return;
      lastSent = body;
      try {
        fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body, credentials: 'same-origin' }).catch(function () {});
      } catch (e) {}
    }, size ? 0 : 1500);
  }
  var cartTmr;
  var sentResult = false;
  function track(what, r) {
    try {
      if (what === 'result') { if (sentResult) return; sentResult = true; }
      var push = window.__feralSitePush;
      if (typeof push === 'function') push({ t: 'fit_' + what, chart: CFG.chart, size: r && r.size || null, conf: r && r.confidence != null ? Math.round(r.confidence * 100) : null });
    } catch (e) {}
  }

  // ── wire up ──────────────────────────────────────────────────────────────
  var api = { open: open, close: close, profile: function () { return P; }, result: function () { return last; }, chart: chart };
  mounted[CFG.chart] = api;
  if (opts.page !== false) {
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-ffit-open], [data-fp-size-guide]');
      if (!t) return;
      e.preventDefault();
      open(t.getAttribute('data-ffit-open') || 'chart');
    });
    paintBar();
    // the product page re-renders pill state on variant change; keep the dot on our pill
    pillsRoot.addEventListener('change', function () { if (last) setTimeout(function () { markPill(last.size); }, 0); });
    window.FeralFitUI = api;
  }
  return api;
  }

  window.FeralFitMount = function (cfg) { return mount(cfg, { page: false }); };
  var host = document.querySelector('[data-ffit]');
  if (host) {
    try { mount(JSON.parse(host.querySelector('[data-ffit-config]').textContent)); } catch (e) {}
  }
})();
